import axiosInstance from './axiosInstance';

export interface CollectionRow {
  id: string;
  name: string;
  collectionType: string;
  gameIds: string[];
  articleId?: string;
  description?: string;
  upvoteCount?: number;
  downvoteCount?: number;
  order?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CollectionArticleRow {
  id: string;
  collectionId: string;
  title: string;
  content: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class CollectionApi {
  static async list(skip = 0, limit = 20, query?: { name?: string; collectionType?: string }): Promise<{ list: CollectionRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/collection', {
      params: {
        skip,
        limit,
        name: query?.name || undefined,
        collectionType: query?.collectionType || undefined,
      },
    });
    return response.data.data;
  }

  static async create(
    payload: {
      name: string;
      collectionType: string;
      gameIds?: string[];
      description?: string;
      upvoteCount?: number;
      downvoteCount?: number;
    },
  ): Promise<CollectionRow> {
    const response = await axiosInstance.post('/cms/v1/collection', payload);
    return response.data.data.collection;
  }

  static async update(
    id: string,
    payload: Partial<
      Pick<CollectionRow, 'name' | 'collectionType' | 'gameIds' | 'description' | 'upvoteCount' | 'downvoteCount' | 'order'>
    >,
  ): Promise<CollectionRow> {
    const response = await axiosInstance.put(`/cms/v1/collection/${id}`, payload);
    return response.data.data.collection;
  }

  static async updateOrder(
    collectionOrders: Array<{ id: string; order: number }>,
  ): Promise<CollectionRow[]> {
    const response = await axiosInstance.put('/cms/v1/collection', {
      collectionOrders,
    });
    return response.data.data.list;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/collection/${id}`);
  }

  static async addGame(id: string, gameId: string): Promise<CollectionRow> {
    const response = await axiosInstance.post(`/cms/v1/collection/${id}/game`, { gameId });
    return response.data.data.collection;
  }

  static async removeGame(id: string, gameId: string): Promise<CollectionRow> {
    const response = await axiosInstance.delete(`/cms/v1/collection/${id}/game`, {
      data: { gameId },
    });
    return response.data.data.collection;
  }

  static async getArticle(id: string): Promise<CollectionArticleRow | null> {
    const response = await axiosInstance.get(`/cms/v1/collection/${id}/article`);
    return response.data.data.article;
  }

  static async saveArticle(id: string, payload: { articleId?: string; title: string; content: string }): Promise<CollectionArticleRow> {
    const response = await axiosInstance.post(`/cms/v1/collection/${id}/article`, payload);
    return response.data.data.article;
  }

  static async listArticles(skip = 0, limit = 20, query?: { collectionId?: string; title?: string }): Promise<{ list: CollectionArticleRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/collection-article', {
      params: {
        skip,
        limit,
        collectionId: query?.collectionId || undefined,
        title: query?.title || undefined,
      },
    });
    return response.data.data;
  }

  static async updateArticleById(id: string, payload: Partial<Pick<CollectionArticleRow, 'title' | 'content'>>): Promise<CollectionArticleRow> {
    const response = await axiosInstance.put(`/cms/v1/collection-article/${id}`, payload);
    return response.data.data.article;
  }

  static async removeArticleById(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/collection-article/${id}`);
  }
}
